/* eslint-disable no-use-before-define */
/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable quotes */
import React, { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import PropTypes from "prop-types";
import {
  Button,
  Pane,
  Accordion,
  AccordionSet,
  Checkbox,
  Loading,
  ConfirmationModal,
  Row,
  Col,
  InfoPopover,
  KeyValue,
  Icon,
} from "@folio/stripes/components";
import { useOkapiKy, useCallout, useStripes } from "@folio/stripes/core";

import "./ArabicLanguageSettings.css";

const ArabicLanguageSettings = () => {
  const intl = useIntl();
  const ky = useOkapiKy();
  const callout = useCallout();
  const stripes = useStripes();

  // Check permissions for language configuration
  const hasLanguageConfigPermission = stripes?.hasPerm?.('search.config.languages.collection.get') || false;
  const hasLanguageConfigWritePermission = stripes?.hasPerm?.('search.config.languages.item.post') || false;


  const [arabicConfig, setArabicConfig] = useState({
    code: "ara",
    enabled: false,
    languageAnalyzer: "arabic",
  });
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [existingConfig, setExistingConfig] = useState(null);
  const [moduleAvailable, setModuleAvailable] = useState(true);

  // Fetch current language configurations
  useEffect(() => {
    if (hasLanguageConfigPermission) {
      fetchLanguageConfigs();
    } else {
      setLoading(false);
    }
  }, [hasLanguageConfigPermission]);

  const fetchLanguageConfigs = async () => {
    try {
      setLoading(true);

      // Check if mod-search module is available by testing the base endpoint
      try {
        await ky.get("search/languages").json();
      } catch (moduleError) {
        console.error('mod-search module not available:', moduleError);

        // Check if it's a module not found error
        if (moduleError.message?.includes('No suitable module found') ||
            moduleError.status === 404 ||
            moduleError.message?.includes('path and tenant')) {
          setModuleAvailable(false);
          if (callout) {
            callout.sendCallout({
              type: "error",
              message: intl.formatMessage({
                id: "ui-plugin-inventory-templates.arabicLanguageSettings.moduleNotAvailable",
              }),
            });
          }
          return;
        }
        throw moduleError; // Re-throw if it's a different error
      }

      // If we reach here, the module is available
      const supportedLangsResponse = await ky.get("search/languages").json();
      setSupportedLanguages(supportedLangsResponse.languages || []);

      const isArabicSupported = supportedLangsResponse.languages?.includes("ara");
      if (!isArabicSupported) {
        if (callout) {
          callout.sendCallout({
            type: "error",
            message: intl.formatMessage({
              id: "ui-plugin-inventory-templates.arabicLanguageSettings.notSupported",
            }),
          });
        }
        return;
      }

      // Get current language configurations
      const response = await ky.get("search/config/languages").json();
      const arabicLang = response.languageConfigs?.find(
        (lang) => lang.code === "ara",
      );

      if (arabicLang) {
        setArabicConfig({ ...arabicLang, enabled: true });
        setExistingConfig(arabicLang);
      }
    } catch (error) {
      console.error('Language config fetch error:', error);

      if (callout) {
        let errorMessage = "ui-plugin-inventory-templates.arabicLanguageSettings.fetchError";

        // Handle specific error types
        if (error.message?.includes('No suitable module found') ||
            error.message?.includes('path and tenant')) {
          errorMessage = "ui-plugin-inventory-templates.arabicLanguageSettings.moduleNotAvailable";
        } else if (error.message?.includes('permission')) {
          errorMessage = "ui-plugin-inventory-templates.arabicLanguageSettings.permissionError";
        } else if (error.status === 403) {
          errorMessage = "ui-plugin-inventory-templates.arabicLanguageSettings.permissionError";
        } else if (error.status === 401) {
          errorMessage = "ui-plugin-inventory-templates.arabicLanguageSettings.authError";
        }

        callout.sendCallout({
          type: "error",
          message: intl.formatMessage({ id: errorMessage }),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const saveArabicConfig = async () => {
    if (!hasLanguageConfigWritePermission) {
      if (callout) {
        callout.sendCallout({
          type: "error",
          message: intl.formatMessage({ id: "ui-plugin-inventory-templates.arabicLanguageSettings.permissionError" }),
        });
      }
      return;
    }

    try {
      setSaving(true);

      // Validate that Arabic is supported
      if (!supportedLanguages.includes("ara")) {
        callout.sendCallout({
          type: "error",
          message: intl.formatMessage({ id: "ui-plugin-inventory-templates.arabicLanguageSettings.notSupported" }),
        });
        return;
      }

      if (arabicConfig.enabled && !existingConfig) {
        // Create new Arabic language configuration
        const configToSave = {
          code: "ara",
          ...(arabicConfig.languageAnalyzer && { languageAnalyzer: arabicConfig.languageAnalyzer })
        };

        await ky.post("search/config/languages", {
          json: configToSave,
        });

        callout.sendCallout({
          type: "success",
          message: intl.formatMessage({ id: "ui-plugin-inventory-templates.arabicLanguageSettings.created" }),
        });
      } else if (arabicConfig.enabled && existingConfig) {
        // Update existing configuration
        const configToUpdate = {
          code: "ara",
          ...(arabicConfig.languageAnalyzer && { languageAnalyzer: arabicConfig.languageAnalyzer })
        };

        await ky.put(`search/config/languages/ara`, {
          json: configToUpdate,
        });

        callout.sendCallout({
          type: "success",
          message: intl.formatMessage({ id: "ui-plugin-inventory-templates.arabicLanguageSettings.updated" }),
        });
      } else if (!arabicConfig.enabled && existingConfig) {
        // Delete configuration
        setShowConfirmDelete(true);
        return;
      }

      await fetchLanguageConfigs();
    } catch (error) {
      callout.sendCallout({
        type: "error",
        message: intl.formatMessage({ id: "ui-plugin-inventory-templates.arabicLanguageSettings.saveError" }),
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteArabicConfig = async () => {
    if (!hasLanguageConfigWritePermission) {
      if (callout) {
        callout.sendCallout({
          type: "error",
          message: intl.formatMessage({ id: "ui-plugin-inventory-templates.arabicLanguageSettings.permissionError" }),
        });
      }
      return;
    }

    try {
      setSaving(true);
      await ky.delete(`search/config/languages/ara`);

      setArabicConfig({
        code: "ara",
        enabled: false,
        languageAnalyzer: "arabic",
      });
      setExistingConfig(null);
      setShowConfirmDelete(false);

      callout.sendCallout({
        type: "success",
        message: intl.formatMessage({ id: "ui-plugin-inventory-templates.arabicLanguageSettings.deleted" }),
      });
    } catch (error) {
      callout.sendCallout({
        type: "error",
        message: intl.formatMessage({
          id: "ui-plugin-inventory-templates.arabicLanguageSettings.deleteError",
        }),
      });
    } finally {
      setSaving(false);
      setShowConfirmDelete(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  // Show module not available error
  if (!moduleAvailable) {
    return (
      <Pane
        defaultWidth="fill"
        fluidContentWidth
        paneTitle={<FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.title" />}
      >
        <div className="arabic-language-settings">
          <div className="arabic-language-card">
            <div className="permission-error">
              <Icon icon="times-circle" size="large" />
              <h3>
                <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.moduleNotAvailable" />
              </h3>
              <p>
                <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.moduleNotAvailableDetails" />
              </p>
            </div>
          </div>
        </div>
      </Pane>
    );
  }

  // Show permission error if user doesn't have access
  if (!hasLanguageConfigPermission) {
    return (
      <Pane
        defaultWidth="fill"
        fluidContentWidth
        paneTitle={<FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.title" />}
      >
        <div className="arabic-language-settings">
          <div className="arabic-language-card">
            <div className="permission-error">
              <Icon icon="exclamation-triangle" size="large" />
              <h3>
                <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.permissionError" />
              </h3>
              <p>
                <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.permissionDetails" />
              </p>
            </div>
          </div>
        </div>
      </Pane>
    );
  }

  return (
    <Pane
      defaultWidth="fill"
      fluidContentWidth
      paneTitle={<FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.title" />}
    >
      <div className="arabic-language-settings">
        <div className="arabic-language-card">
          <div className="arabic-language-header">
            <Icon icon="globe" size="medium" />
            <h3>
              <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.configuration" />
            </h3>
          </div>

          <AccordionSet>
            <Accordion
              label={
                <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.basicConfiguration" />
              }
              id="arabic-basic-config"
            >
              <Row>
                <Col xs={12}>
                  <Checkbox
                    label={
                      <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.enable" />
                    }
                    checked={arabicConfig.enabled}
                    onChange={(e) =>
                      setArabicConfig({
                        ...arabicConfig,
                        enabled: e.target.checked,
                      })
                    }
                    disabled={saving || !hasLanguageConfigWritePermission}
                  />
                  <InfoPopover
                    content={
                      <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.enableTooltip" />
                    }
                  />
                </Col>
              </Row>

              <Row>
                <Col xs={6}>
                  <KeyValue
                    label={
                      <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.status" />
                    }
                    value={
                      existingConfig ? (
                        <span className="arabic-status-active">
                          <Icon icon="check-circle" />
                          <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.active" />
                        </span>
                      ) : (
                        <span>
                          <Icon icon="times-circle" />
                          <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.inactive" />
                        </span>
                      )
                    }
                  />
                </Col>
                <Col xs={6}>
                  <KeyValue
                    label={
                      <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.analyzer" />
                    }
                    value={existingConfig?.languageAnalyzer || "arabic (default)"}
                  />
                </Col>
              </Row>

              {supportedLanguages.length > 0 && (
                <Row>
                  <Col xs={12}>
                    <KeyValue
                      label={
                        <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.supportedLanguages" />
                      }
                      value={supportedLanguages.join(", ")}
                    />
                  </Col>
                </Row>
              )}
            </Accordion>

            <Accordion
              label={<FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.features" />}
              id="arabic-features"
            >
              <Row>
                <Col xs={12}>
                  <div className="arabic-features-list">
                    <h4>
                      <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.supportedFeatures" />
                    </h4>
                    <ul>
                      <li>
                        <Icon icon="check-circle" />
                        <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.feature.stemming" />
                      </li>
                      <li>
                        <Icon icon="check-circle" />
                        <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.feature.normalization" />
                      </li>
                      <li>
                        <Icon icon="check-circle" />
                        <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.feature.tokenization" />
                      </li>
                      <li>
                        <Icon icon="check-circle" />
                        <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.feature.diacritics" />
                      </li>
                    </ul>
                  </div>
                </Col>
              </Row>
            </Accordion>

            <Accordion
              label={
                <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.technicalDetails" />
              }
              id="arabic-technical"
            >
              <Row>
                <Col xs={6}>
                  <KeyValue
                    label={
                      <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.tokenizer" />
                    }
                    value="icu_tokenizer"
                  />
                </Col>
                <Col xs={6}>
                  <KeyValue
                    label={
                      <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.filters" />
                    }
                    value="arabic_normalization, arabic_stemmer, lowercase"
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <KeyValue
                    label={
                      <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.charFilters" />
                    }
                    value="arabic_diacritics_removal, arabic_text_normalization"
                  />
                </Col>
              </Row>
            </Accordion>
          </AccordionSet>
        </div>

        <div className="arabic-language-actions">
          <Button
            buttonStyle="primary"
            onClick={saveArabicConfig}
            disabled={saving || !hasLanguageConfigWritePermission}
            loading={saving}
          >
            <FormattedMessage id="ui-plugin-inventory-templates.save" />
          </Button>
        </div>
      </div>

      <ConfirmationModal
        open={showConfirmDelete}
        heading={
          <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.deleteConfirm.heading" />
        }
        message={
          <FormattedMessage id="ui-plugin-inventory-templates.arabicLanguageSettings.deleteConfirm.message" />
        }
        confirmLabel={<FormattedMessage id="ui-plugin-inventory-templates.delete" />}
        cancelLabel={<FormattedMessage id="ui-plugin-inventory-templates.cancel" />}
        onConfirm={deleteArabicConfig}
        onCancel={() => setShowConfirmDelete(false)}
      />
    </Pane>
  );
};

ArabicLanguageSettings.propTypes = {};

export default ArabicLanguageSettings;
